import { css, styled } from "goober";
import { useRef, useState } from "react";
import {
  desktopViewportFrameCss,
  useDesktopViewportScale,
} from "../../hooks/useDesktopViewport";
import { CircleMesh } from "../CircleMesh";
import { BrowserChrome } from "../BrowserChrome";
import { HtmlViewer, type HtmlViewerHandle } from "../HtmlViewer";
import { Icon } from "../Icon";
import { Tooltip } from "../Tooltip";
import useInspectMode, {
  type InspectOptions,
} from "../LiveBrowser/useInspectMode";
import { useInspectShortcut } from "../../hooks/useInspectShortcut";
import { EmptyState } from "../EmptyState";
import { WavyLine } from "../WavyLine";
import type { FileRenderControl } from "./types";
import { useFileActions } from "./useFileActions";

const StyledHtml = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const bleedCss = (bleed: number) => css`
  margin: -${bleed}px;
  width: calc(100% + ${bleed * 2}px);
  height: calc(100% + ${bleed * 2}px);
`;

const Background = styled("div")`
  position: absolute;
  inset: 0;
`;

const PreviewContainer = styled("div")`
  position: relative;
  z-index: 1;
  width: 80%;
  max-width: 600px;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  overflow: hidden;
`;

const frame = css`
  ${desktopViewportFrameCss}
  display: block;
  border: none;
  background: transparent;
`;

// The interactive variant is a plain browser-like viewport: the page fills the
// panel at its own width and scrolls natively, rather than being scaled down
// into the preview card.
const InteractiveContainer = styled("div")`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: white;
`;

const interactiveFrame = css`
  display: block;
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

type Props = {
  url: string;
  output: string;
  blob?: Blob;
  fileName?: string;
  bleed?: number;
  baseRoute?: string;
  interactive?: boolean;
  loading?: boolean;
  inspection?: Omit<InspectOptions, "enabled">;
  onReload?: () => void;
  onControls?: (controls: FileRenderControl[]) => void;
};

export function Html({
  url,
  output,
  blob,
  fileName,
  bleed,
  baseRoute,
  interactive,
  loading = false,
  inspection,
  onReload,
  onControls,
}: Props) {
  useFileActions({
    url,
    fileName: fileName ?? "output",
    onControls: blob ? onControls : undefined,
  });
  const previewContainerRef = useDesktopViewportScale<HTMLDivElement>();
  const viewerRef = useRef<HtmlViewerHandle>(null);
  const [inspectMode, setInspectMode] = useState(false);
  const canInspect =
    !loading && interactive && inspection?.onElementSelect != null;
  const inspect = useInspectMode({
    ...inspection,
    enabled: canInspect && inspectMode,
  });
  useInspectShortcut(() => {
    if (canInspect) setInspectMode((previous) => !previous);
  });

  if (interactive) {
    const fileUrl = new URL(url, location.href);
    const openUrl =
      !blob && (fileUrl.protocol === "http:" || fileUrl.protocol === "https:")
        ? fileUrl.href
        : undefined;
    return (
      <InteractiveContainer className={bleed ? bleedCss(bleed) : undefined}>
        <BrowserChrome
          displayUrl={baseRoute ?? fileName ?? "Webpage preview"}
          openUrl={loading ? undefined : openUrl}
          access={
            !loading &&
            openUrl && (
              <Tooltip content="Opening this link requires access to the file.">
                <span aria-label="File access" style={{ display: "flex" }}>
                  <Icon name="lock" size={12} color="var(--text-secondary)" />
                </span>
              </Tooltip>
            )
          }
          onBack={() => viewerRef.current?.back()}
          onForward={() => viewerRef.current?.forward()}
          onReload={loading ? undefined : onReload}
          canBack={!loading}
          canForward={!loading}
          inspectMode={inspectMode}
          onInspectModeChange={canInspect ? setInspectMode : undefined}
          embedded
        >
          {loading ? (
            <EmptyState
              icon={<WavyLine />}
              title="Loading page…"
              maxWidth={220}
            />
          ) : (
            <HtmlViewer
              apiRef={viewerRef}
              html={output}
              blob={blob}
              title={fileName ?? "Webpage preview"}
              className={interactiveFrame}
              baseRoute={baseRoute}
              interactive
              inspectFeature={canInspect ? inspect.feature : undefined}
            />
          )}
        </BrowserChrome>
      </InteractiveContainer>
    );
  }

  return (
    <StyledHtml className={bleed ? bleedCss(bleed) : undefined}>
      <Background>
        <CircleMesh mutingIntensity={1} seed="html-preview" />
      </Background>
      <PreviewContainer ref={previewContainerRef}>
        <HtmlViewer html={output} className={frame} baseRoute={baseRoute} />
      </PreviewContainer>
    </StyledHtml>
  );
}
